/**
 * PatternIQ Agent Feedback - Admin Dashboard
 * ===========================================
 * View and analyze all Agent Hub feedback with real-time updates
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquareText, TrendingUp, AlertCircle } from 'lucide-react';
import FeedbackAnalytics from '@/components/admin/FeedbackAnalytics';
import FeedbackTable from '@/components/admin/FeedbackTable';

export default function CopilotFeedback() {
  const [activeTab, setActiveTab] = useState('analytics');

  // Get negative feedback count for badge
  const { data: recentNegative } = useQuery({
    queryKey: ['copilot-feedback-negative-count'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/copilot/feedback/list?rating=negative&limit=100');
        if (!response.ok) return 0;
        const data = await response.json();
        return data.data?.pagination?.total || 0;
      } catch (error) {
        console.error('Failed to fetch negative feedback count:', error);
        return 0;
      }
    },
    refetchInterval: 30000,
    retry: false, // Don't retry if API fails
  });

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <MessageSquareText className="h-8 w-8 text-blue-600" />
          Agent Feedback
        </h1>
        <p className="text-gray-600 mt-1">
          Analyze user feedback from Agent Hub and improve agent responses
        </p>
      </div>

      {/* Alert Banner for Negative Feedback */}
      {recentNegative && recentNegative > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-900">
                  {recentNegative} negative feedback item{recentNegative !== 1 ? 's' : ''} requiring attention
                </p>
                <p className="text-sm text-amber-700">
                  Review the feedback list to see what users are struggling with
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto">
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-2 relative">
            <MessageSquareText className="h-4 w-4" />
            Feedback List
            {recentNegative && recentNegative > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {recentNegative > 99 ? '99+' : recentNegative}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          <FeedbackAnalytics />
        </TabsContent>

        <TabsContent value="feedback" className="space-y-4">
          <FeedbackTable />
        </TabsContent>
      </Tabs>
    </div>
  );
}

